import React, { useState } from "react";
import toast from "react-hot-toast";
import LegacyLogo from "../../assets/legacy grey.png";
import ellipse from "../../assets/ellipse-1@2x.png";
import { useNavigate } from "react-router-dom";
import ProfileModal from "../Modals/ProfileModal";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { base_url } from "../../Global";
import styles from "./Navbar.module.css";

const Navbar = ({ tab, changeTab }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [updateOpen, setUpdateOpen] = useState(false);

  // Edit profile form fields
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [currentlyPassword, setCurrentlyPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  const closeModal = () => {
    setModalOpen(false);
    setUpdateOpen(false);
    setCurrentlyPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
  };

  const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const handleMyAccount = () => {
    handleCloseMenu();
    const user = JSON.parse(localStorage.getItem("user"));
    setUserName(user.username);
    setUserEmail(user.email);
    setNewUserName(user.username);
    setNewUserEmail(user.email);
    setModalOpen(true);
  };

  const handleCancelEdit = () => {
    // Reset form fields back to current saved values
    setNewUserName(userName);
    setNewUserEmail(userEmail);
    setCurrentlyPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setUpdateOpen(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (newPassword && newPassword !== confirmNewPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword && newPassword === currentlyPassword) {
      toast.error("New password cannot be the same as the current password");
      return;
    }

    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const res = await fetch(`${base_url}/auth/updateProfile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          authorization: accessToken,
        },
        body: JSON.stringify({
          username: newUserName,
          email: newUserEmail,
          password: currentlyPassword,
          ...(newPassword && { newPassword }),
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update profile");

      // Sync localStorage
      const storedUser = JSON.parse(localStorage.getItem("user"));
      localStorage.setItem(
        "user",
        JSON.stringify({
          ...storedUser,
          username: newUserName,
          email: newUserEmail,
        }),
      );

      setUserName(newUserName);
      setUserEmail(newUserEmail);
      toast.success("Profile updated successfully!");
      handleCancelEdit();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ProfileModal isOpen={isModalOpen} onClose={closeModal}>
        <div className="space-y-4">
          {/* Avatar */}
          <div className={styles.imageProfileBox}>
            <img
              className="h-[3.25rem] w-[3.25rem] rounded-[50%] object-cover"
              loading="lazy"
              alt=""
              src={ellipse}
            />
          </div>

          {updateOpen ? (
            /* ── Edit form ── */
            <form onSubmit={handleUpdate} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  type="text"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  placeholder="Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="Email"
                />
              </div>

              <hr className="border-gray-200" />

              <div>
                <label className="block text-sm font-medium mb-1">
                  New password
                </label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Leave blank to keep current"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Confirm new password
                </label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="Repeat new password"
                />
              </div>

              <hr className="border-gray-200" />

              <div>
                <label className="block text-sm font-medium mb-1">
                  Current password <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  type="password"
                  value={currentlyPassword}
                  onChange={(e) => setCurrentlyPassword(e.target.value)}
                  placeholder="Required to save any changes"
                  required
                />
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  className="flex-1 px-4 py-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-400 focus:outline-none"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 px-4 py-2 rounded-md border-none ${
                    loading ? "bg-gray-400" : "bg-black hover:bg-gray-800"
                  } text-white focus:outline-none`}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          ) : (
            /* ── Read-only view ── */
            <div className="space-y-2">
              <p>
                <strong>Name:</strong> {userName}
              </p>
              <p>
                <strong>Email:</strong> {userEmail}
              </p>
              <button
                className="w-full mt-2 px-4 py-2 rounded-md border-none bg-black hover:bg-gray-800 text-white focus:outline-none"
                onClick={() => setUpdateOpen(true)}
              >
                Edit profile
              </button>
            </div>
          )}
        </div>
      </ProfileModal>

      <header className="self-stretch bg-black w-full fixed z-[10] left-0 top-0 px-6 py-3 flex flex-row items-start justify-start box-border shrink-0">
        <div className="flex-1 rounded-xl bg-black flex flex-row items-center justify-between py-[0.688rem] pr-[0.688rem] pl-[1.375rem] box-border max-w-full gap-[1.25rem]">
          {/* Logo / tab */}
          <div className="flex items-center">
            <div
              className={`flex justify-center items-center px-3 rounded-full transition delay-150 duration-1000 cursor-pointer ${
                tab === "legacy" ? "bg-black" : "bg-transparent"
              }`}
              onClick={() => changeTab("legacy")}
            >
              <img
                className="h-12 w-40 relative object-cover z-[1] cursor-pointer"
                loading="lazy"
                alt=""
                src={LegacyLogo}
              />
            </div>
          </div>

          {/* Avatar + menu */}
          <div className="flex flex-row items-center justify-start">
            <IconButton
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={handleOpenMenu}
            >
              <img
                className="h-[3.25rem] w-[3.25rem] relative rounded-[50%] object-cover z-[1]"
                loading="lazy"
                alt=""
                src={ellipse}
              />
            </IconButton>
            <Menu
              id="profile-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleMyAccount}>My account</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </div>
        </div>
      </header>
    </>
  );
};

export default Navbar;
